import {
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaHourglassHalf,
  FaMapMarkerAlt,
  FaUser,
  FaWrench,
} from 'react-icons/fa';
import { IconType } from 'react-icons';

// Define strict types for resource statuses
export type ResourceStatus =
  | 'in_transit'
  | 'available'
  | 'assigned'
  | 'os_rest'
  | 'os_personnel'
  | 'os_mechanical'
  | 'etr'
  | 'in_process'
  | 'checked_out';

type StatusConfig = {
  label: string;
  color: string;
  icon: IconType;
  bgColor: string;
};

// Status configurations with labels, colors, and icons
export const STATUS_CONFIG: Record<ResourceStatus, StatusConfig> = {
  in_transit: {
    label: 'In Transit',
    color: 'info',
    icon: FaMapMarkerAlt,
    bgColor: '#17a2b8',
  },
  available: {
    label: 'Available',
    color: 'success',
    icon: FaCheck,
    bgColor: '#28a745',
  },
  assigned: {
    label: 'Assigned',
    color: 'danger',
    icon: FaUser,
    bgColor: '#dc3545',
  },
  os_rest: {
    label: 'O/S Rest',
    color: 'warning',
    icon: FaClock,
    bgColor: '#ffc107',
  },
  os_personnel: {
    label: 'O/S Personnel',
    color: 'warning',
    icon: FaExclamationTriangle,
    bgColor: '#fd7e14',
  },
  os_mechanical: {
    label: 'O/S Mechanical',
    color: 'secondary',
    icon: FaWrench,
    bgColor: '#6c757d',
  },
  etr: {
    label: 'ETR',
    color: 'info',
    icon: FaHourglassHalf,
    bgColor: '#17a2b8',
  },
  in_process: {
    label: 'Checkout In Process',
    color: 'warning',
    icon: FaHourglassHalf,
    bgColor: '#fd7e14',
  },
  checked_out: {
    label: 'Checked Out',
    color: 'secondary',
    icon: FaCheck,
    bgColor: '#6c757d',
  },
} as const;

// Status labels mapping (simplified version for display)
export const STATUS_LABELS: Record<string, string> = Object.entries(
  STATUS_CONFIG
).reduce(
  (acc, [key, config]) => ({
    ...acc,
    [key]: config.label,
  }),
  {}
);

// Source type configurations
export const SOURCE_CONFIG = {
  REQUESTED: { label: 'REQUESTED', color: 'primary' },
  TRANSIT: { label: 'TRANSIT', color: 'info' },
  'MUTUAL AID': { label: 'MUTUAL AID', color: 'warning' },
} as const;

// Column IDs for the kanban board
export const COLUMN_IDS = {
  IN_TRANSIT: 'in_transit',
  CHECKED_IN: 'checked_in',
  CHECKED_OUT: 'checked_out',
} as const;

// Resource statuses
export const RESOURCE_STATUSES = {
  IN_TRANSIT: 'in_transit',
  AVAILABLE: 'available',
  ASSIGNED: 'assigned',
  OS_REST: 'os_rest',
  OS_PERSONNEL: 'os_personnel',
  OS_MECHANICAL: 'os_mechanical',
  ETR: 'etr',
  IN_PROCESS: 'in_process',
  CHECKED_OUT: 'checked_out',
} as const;

// Checked-in statuses (for validation)
export const CHECKED_IN_STATUSES = [
  RESOURCE_STATUSES.AVAILABLE,
  RESOURCE_STATUSES.ASSIGNED,
  RESOURCE_STATUSES.IN_TRANSIT,
  RESOURCE_STATUSES.OS_REST,
  RESOURCE_STATUSES.OS_PERSONNEL,
  RESOURCE_STATUSES.OS_MECHANICAL,
  RESOURCE_STATUSES.ETR,
] as const;

// Status filter options for checked-in resources
export const CHECKED_IN_STATUS_OPTIONS = [
  { value: 'all', label: 'All Checked In' },
  { value: RESOURCE_STATUSES.AVAILABLE, label: STATUS_CONFIG.available.label },
  { value: RESOURCE_STATUSES.ASSIGNED, label: STATUS_CONFIG.assigned.label },
  { value: RESOURCE_STATUSES.OS_REST, label: STATUS_CONFIG.os_rest.label },
  {
    value: RESOURCE_STATUSES.OS_PERSONNEL,
    label: STATUS_CONFIG.os_personnel.label,
  },
  {
    value: RESOURCE_STATUSES.OS_MECHANICAL,
    label: STATUS_CONFIG.os_mechanical.label,
  },
  { value: RESOURCE_STATUSES.ETR, label: STATUS_CONFIG.etr.label },
] as const;

// Status filter options for checked-out resources
export const CHECKED_OUT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Checked Out' },
  {
    value: RESOURCE_STATUSES.CHECKED_OUT,
    label: STATUS_CONFIG.checked_out.label,
  },
  {
    value: RESOURCE_STATUSES.IN_PROCESS,
    label: STATUS_CONFIG.in_process.label,
  },
] as const;
